import { ArrowUpRight, BookOpen, Globe2 } from "lucide-react";
import { FadeUp, MaskLineInView } from "./motion";
import CoverGallery from "./CoverGallery";
import Library from "./Library";
import { AUTHOR_AMAZON } from "../data/library";

const FLAGSHIP_COVER = "/assets/lexicon_poster.webp";

const RETAILERS = [
  "Amazon — Kindle & Audible",
  "Apple",
  "Google Play",
  "Samsung",
  "Walmart",
  "ThriftBooks",
  "Half Price Books",
];

export default function Books() {
  return (
    <section
      id="books"
      className="relative border-t border-line bg-ink2/40 py-20 sm:py-28"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <FadeUp className="mb-16 flex items-end justify-between gap-6">
          <div>
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion">
              03 — The Library
            </p>
            <h2 className="font-serif text-4xl font-light leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
              <MaskLineInView>Sixty books.</MaskLineInView>
              <MaskLineInView delay={0.08}>
                One record breaker<span className="text-vermilion">.</span>
              </MaskLineInView>
            </h2>
          </div>
          <p className="hidden max-w-xs text-right font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-smoke md:block">
            As of 2026 · Global reach ·
            <br />
            Print, eBook & Audio
          </p>
        </FadeUp>

        <FadeUp
          data-testid="book-card-1"
          className="group grid overflow-hidden border border-line bg-ink lg:grid-cols-2"
        >
          <div className="relative overflow-hidden border-b border-line lg:border-b-0 lg:border-r">
            <img
              src={FLAGSHIP_COVER}
              alt="Aether Verge Collective — The All-Encompassing Programming Language Lexicon"
              className="h-full min-h-[24rem] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <div className="pointer-events-none absolute inset-0 spotlight" />
            <span className="absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-vermilion" />
            <span className="absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-vermilion" />
          </div>
          <div className="flex flex-col justify-between gap-10 p-8 sm:p-12">
            <div>
              <p className="mb-6 inline-flex items-center gap-3 border border-vermilion/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion">
                <BookOpen className="h-3.5 w-3.5" strokeWidth={1.5} />
                Record-breaking flagship
              </p>
              <h3 className="font-serif text-3xl font-light leading-tight tracking-tight text-bone sm:text-5xl">
                Aether Verge Collective
              </h3>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ash">
                The All-Encompassing Programming Language Lexicon
              </p>
              <p className="mt-8 font-serif text-6xl font-light text-vermilion sm:text-7xl">
                3.2M <span className="font-serif text-2xl italic text-ash">words</span>
              </p>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ash">
                A record-breaking, three-million-word A-to-Z computer
                programming collection — 41 illustrated volumes, one per
                language, from X++ to TypeScript — among the most ambitious
                single-author publishing feats on record.
              </p>
            </div>
            <div className="space-y-6 border-t border-line pt-8">
              <a
                href={AUTHOR_AMAZON}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="amazon-author-link"
                className="group/btn inline-flex items-center gap-3 border border-vermilion bg-vermilion px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ink transition-all duration-300 hover:bg-transparent hover:text-vermilion"
              >
                Shop all titles on Amazon
                <ArrowUpRight
                  className="h-4 w-4 transition-transform duration-300 group-hover/btn:-translate-y-1 group-hover/btn:translate-x-1"
                  strokeWidth={1.5}
                />
              </a>
              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                  Available at — Retailers
                </p>
                <div className="flex flex-wrap gap-2">
                  {RETAILERS.map((r) => (
                    <span
                      key={r}
                      className="border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ash"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Globe2 className="h-4 w-4 shrink-0 text-vermilion" strokeWidth={1.5} />
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                  Wholesale — Ingram & international distribution networks
                </p>
              </div>
            </div>
          </div>
        </FadeUp>

        <CoverGallery />
        <Library />

        <FadeUp
          delay={0.2}
          className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-line pt-10 sm:flex-row sm:items-center"
        >
          <p className="max-w-xl text-lg leading-relaxed text-ash">
            Across 58 published titles and counting — psychology, technology,
            and narrative fiction — a signature style described as{" "}
            <span className="font-serif italic text-bone">
              intellectually robust
            </span>
            , leveraging psychology to craft complex narratives and intricate
            character dynamics.
          </p>
          <p className="font-serif text-2xl italic text-smoke">
            Sold worldwide<span className="text-vermilion">.</span>
          </p>
        </FadeUp>
      </div>
    </section>
  );
}